import { getLocale } from '@borealis/aurora';
import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react/macro';
import '@warp-ds/components/icon';
import { Button, Modal, TextArea } from '@warp-ds/react';
import { useEffect, useState } from 'react';

interface ImageDescription {
  index: number;
  description: string;
}

interface DescriptionModalProps {
  open: boolean;
  onSave: (descriptions: ImageDescription[]) => void;
  onDismiss: () => void;
  allImages: string[];
  initialImageIndex: number;
  initialDescriptions: string[];
}

export const DescriptionModal = ({
  open,
  onSave,
  onDismiss,
  allImages,
  initialImageIndex,
  initialDescriptions,
}: DescriptionModalProps) => {
  const [activeIndex, setActiveIndex] = useState(initialImageIndex);
  const [localDescriptions, setLocalDescriptions] = useState<string[]>(initialDescriptions);

  // Update local state when modal opens or initial descriptions change
  useEffect(() => {
    if (open) {
      setActiveIndex(initialImageIndex);
      setLocalDescriptions(initialDescriptions);
    }
  }, [open, initialImageIndex, initialDescriptions]);

  const hasMultipleImages = allImages.length > 1;
  const currentDisplayImage = allImages[activeIndex];
  const currentDescription = localDescriptions[activeIndex] || '';

  const textAreaCount = currentDescription.length;

  const updateCurrentDescription = (value: string) => {
    const newDescriptions = [...localDescriptions];
    newDescriptions[activeIndex] = value;
    setLocalDescriptions(newDescriptions);
  };

  const goToPrevious = () => {
    const newIndex = (activeIndex - 1 + allImages.length) % allImages.length;
    setActiveIndex(newIndex);
  };

  const goToNext = () => {
    const newIndex = (activeIndex + 1) % allImages.length;
    setActiveIndex(newIndex);
  };

  const handleSave = () => {
    // Only include descriptions that have been changed from their initial state
    const changedDescriptions: ImageDescription[] = localDescriptions
      .map((description, index) => ({ index, description }))
      .filter((item, index) => item.description !== initialDescriptions[index]);

    onSave(changedDescriptions);
  };

  const handleDismiss = () => {
    // Check if any descriptions have been changed
    const hasChanges = localDescriptions.some((desc, index) => desc !== initialDescriptions[index]);

    if (hasChanges) {
      const changedDescriptions: ImageDescription[] = localDescriptions
        .map((description, index) => ({ index, description }))
        .filter((item, index) => item.description !== initialDescriptions[index]);

      onSave(changedDescriptions);
    } else {
      onDismiss();
    }
  };

  return (
    <Modal
      open={open}
      right
      title={i18n.t({
        id: 'mobility-item-insertion.form.imageUpload.descriptionModalHeader',
        message: 'Image text',
      })}
      onDismiss={handleDismiss}
      // @ts-ignore: Custom style for modal width
      style={{ zIndex: 50, '--w-modal-width': '768px' }}
    >
      <div>
        {Boolean(currentDisplayImage) && (
          <div className="flex items-center justify-center mb-8">
            {hasMultipleImages ? (
              <Button
                type="button"
                quiet
                onClick={goToPrevious}
                className="flex-shrink-0 bg-transparent flex"
                aria-label={i18n.t({
                  id: 'mobility-item-insertion.form.imageUpload.previousImage',
                  message: 'Previous image',
                })}
              >
                <w-icon name="ChevronLeft" size="medium" className="s-icon-primary" locale={getLocale()} />
              </Button>
            ) : (
              <div className="w-8" />
            )}
            <div className="flex-1 flex justify-center mx-8">
              <div className="relative rounded-4 overflow-hidden aspect-3/2 flex-1">
                {/* Foreground actual image */}
                <img
                  src={currentDisplayImage}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  }}
                  alt={i18n.t({
                    id: 'mobility-item-insertion.form.imageUpload.imageAltText',
                    message: 'Image preview',
                  })}
                  className="relative z-10 w-full h-full object-contain"
                />
                {/* Background blurred image */}
                <img
                  aria-hidden="true"
                  src={currentDisplayImage}
                  alt=""
                  className="absolute left-0 top-0 w-full h-full z-0 overflow-hidden object-cover object-center"
                  style={{
                    filter: 'blur(20px)',
                  }}
                />
              </div>
            </div>
            {hasMultipleImages ? (
              <Button
                type="button"
                quiet
                onClick={goToNext}
                className="flex-shrink-0 bg-transparent flex"
                aria-label={i18n.t({
                  id: 'mobility-item-insertion.form.imageUpload.nextImage',
                  message: 'Next image',
                })}
              >
                <w-icon name="ChevronRight" size="medium" className="s-icon-primary" locale={getLocale()} />
              </Button>
            ) : (
              <div className="w-8" />
            )}
          </div>
        )}
        <p className="text-center text-caption font-bold mb-24">
          {activeIndex + 1}/{allImages.length}
        </p>
        <TextArea
          name="add-photo-description"
          label={i18n.t({
            id: 'mobility-item-insertion.form.imageUpload.descriptionLabel',
            message: 'Add image text',
          })}
          optional
          rows={1}
          value={currentDescription}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            updateCurrentDescription(e.target.value);
          }}
          className="mb-16"
          helpText={`${textAreaCount}/255`}
          onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          maxLength={255}
        />
        <div className="flex gap-8 justify-end">
          <Button primary type="button" className="btn btn-primary mb-4" onClick={handleSave}>
            <Trans id="mobility-item-insertion.form.imageUpload.addDescription" comment="Add photo description">
              Done
            </Trans>
          </Button>
        </div>
      </div>
    </Modal>
  );
};
